import CachedIcon from '@mui/icons-material/Cached';
import {
  Box,
  Dialog,
  FormLabel,
  TextField,
  Paper,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AppButton from '../../../../components/ui/AppButton';
import { useUpdateStateShipping } from '../../../../hooks/useShipping';
import { useNotificationStore } from '../../../../stores/notificationStore';

const TrackingDialog = ({ open, onClose, shippingId }) => {
  const queryClient = useQueryClient();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const { mutateAsync: updateStateShipping } = useUpdateStateShipping();

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const validationSchema = Yup.object().shape({
    tracking: Yup.string()
      .min(5, 'กรุณาระบุ Tracking Number ให้ถูกต้อง')
      .required('กรุณากรอก Tracking Number'),
    express: Yup.string().required('กรุณาเลือกบริการจัดส่ง'),
  });

  const formik = useFormik({
    initialValues: {
      tracking: '',
      express: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await updateStateShipping({
          params: {
            id: shippingId,
          },
          dto: {
            tracking_no: values.tracking,
            express: values.express,
          },
        });

        if (response.Code === '20100') {
          setNotification('อัปเดตสถานะการจัดส่งสำเร็จ', 'success');
          queryClient.invalidateQueries({ queryKey: ['shippingList'] });
          handleClose();
        }
      } catch (err) {
        console.error(err);
        setNotification('อัปเดตสถานะการจัดส่งไม่สำเร็จ', 'error');
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '450px',
        },
      }}
    >
      <Paper
        sx={{
          width: '400px',
          backgroundColor: 'white',
          padding: '2rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <CachedIcon sx={{ color: '#0ea5e9' }} />
          <Typography component="h2" sx={{ fontSize: '2rem', fontWeight: '500', color: '#333' }}>
            Add Tracking
          </Typography>
        </Box>
        <Box sx={{ marginBottom: '15px' }}>
          <FormLabel
            sx={{ fontSize: '1.5rem', fontWeight: '500', color: '#333', display: 'block' }}
          >
            กรอกรหัส Tracking
          </FormLabel>
          <TextField
            variant="outlined"
            placeholder="e.g. xxx-xxxxxx-xx"
            autoComplete="off"
            name="tracking"
            value={formik.values.tracking}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.tracking && Boolean(formik.errors.tracking)}
            sx={{
              width: '100%',

              '& .MuiInputBase-input': {
                fontSize: '1.25rem',
                width: '100%',
                height: '30px',
                padding: '5px 10px',
              },
            }}
          />
          {formik.touched.tracking && formik.errors.tracking && (
            <Typography sx={{ fontSize: '1.25rem', color: '#f5222d', marginTop: '4px' }}>
              {formik.errors.tracking}
            </Typography>
          )}
        </Box>
        <Box sx={{ marginBottom: '30px' }}>
          <FormLabel
            id="express"
            sx={{ fontSize: '1.5rem', fontWeight: '500', color: '#333', display: 'block' }}
          >
            เลือก Express
          </FormLabel>
          <Select
            displayEmpty
            labelId="express"
            id="express"
            name="express"
            value={formik.values.express}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.express && Boolean(formik.errors.express)}
            sx={{
              width: '100%',
              '& .MuiSelect-select': {
                fontSize: '1.25rem',
                padding: '5px 10px',
                height: '30px !important',
                lineHeight: '28px',
                color: formik.values.express ? '#333' : '#aaa',
              },
            }}
          >
            <MenuItem disabled value="">
              Express...
            </MenuItem>
            <MenuItem value="kerry express">Kerry Express</MenuItem>
            <MenuItem value="j&t express">J&amp;t Express</MenuItem>
            <MenuItem value="flash express">Flash Express</MenuItem>
            <MenuItem value="best express">Best Express</MenuItem>
            <MenuItem value="ไปรษณีย์ ems">ไปรษณีย์ EMS</MenuItem>
          </Select>
          {formik.touched.express && formik.errors.express && (
            <Typography sx={{ fontSize: '1.25rem', color: '#f5222d', marginTop: '4px' }}>
              {formik.errors.express}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <AppButton
            variant="text"
            onClick={handleClose}
            sx={{
              width: '100px',
              fontSize: '1.25rem',
              color: '#333',
              backgroundColor: '#e5e5e5',

              '&:hover': {
                color: '#333',
                backgroundColor: '#e5e5e5',
                opacity: '0.85',
              },
            }}
          >
            ยกเลิก
          </AppButton>
          <AppButton
            variant="text"
            onClick={formik.handleSubmit}
            sx={{
              width: '100px',
              fontSize: '1.25rem',
              color: 'white',

              backgroundColor: '#005773',
              '&:hover': {
                color: 'white',
                backgroundColor: '#005773',
                opacity: '0.85',
              },
            }}
          >
            ตกลง
          </AppButton>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default TrackingDialog;
